import { useMemo, useState } from "react";
import { getCurrentSession } from "../../services/auth";

const API_URL = (
  process.env.REACT_APP_API_URL || "https://zedro-backend.onrender.com"
).replace(/\/$/, "");

const EMPTY_FORM = {
  stripe_price_id: "",
  amount: "",
  currency: "CAD",
  price_type: "one_time",
  interval: "month",
  label: "",
  marketing_note: "",
  access_duration_days: "0",
  display_order: "0",
  is_active: true,
  is_recommended: false,
};

function authHeaders(includeJson = false) {
  const session = getCurrentSession();
  const headers = {};
  if (includeJson) headers["Content-Type"] = "application/json";
  if (session?.token) headers.Authorization = `Bearer ${session.token}`;
  return headers;
}

function money(amount, currency) {
  const cents = Number(amount || 0);
  const value = Number.isFinite(cents) ? cents / 100 : 0;
  try {
    return new Intl.NumberFormat("es-CA", {
      style: "currency",
      currency: String(currency || "CAD").toUpperCase(),
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${String(currency || "CAD").toUpperCase()}`;
  }
}

function paymentMode(price) {
  if (price.price_type === "recurring") {
    return price.interval === "year" ? "Recurrente · anual" : "Recurrente · mensual";
  }
  return "Pago único";
}

export default function ProductPriceManager({ productId, initialPrices = [] }) {
  const [prices, setPrices] = useState(initialPrices);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyPriceId, setBusyPriceId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const orderedPrices = useMemo(
    () =>
      [...prices].sort((a, b) => {
        if (Boolean(a.is_active) !== Boolean(b.is_active)) {
          return a.is_active ? -1 : 1;
        }
        if (Boolean(a.is_recommended) !== Boolean(b.is_recommended)) {
          return a.is_recommended ? -1 : 1;
        }
        return Number(a.display_order || 0) - Number(b.display_order || 0);
      }),
    [prices]
  );

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function resetMessages() {
    setError("");
    setNotice("");
  }

  async function createPrice(event) {
    event.preventDefault();
    resetMessages();

    const amountNumber = Number(form.amount);
    const amountCents = Math.round(amountNumber * 100);

    if (!Number.isFinite(amountNumber) || amountNumber <= 0 || amountCents <= 0) {
      setError("Ingresa un importe válido mayor que cero.");
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/admin/product-prices`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          product_id: productId,
          stripe_price_id: form.stripe_price_id.trim(),
          amount: amountCents,
          currency: form.currency.trim().toUpperCase(),
          price_type: form.price_type,
          interval: form.price_type === "recurring" ? form.interval : null,
          label: form.label.trim() || null,
          marketing_note: form.marketing_note.trim() || null,
          access_duration_days: Number(form.access_duration_days || 0),
          display_order: Number(form.display_order || 0),
          is_active: form.is_active,
          is_recommended: form.is_active ? form.is_recommended : false,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "No fue posible crear el precio.");
      }

      setPrices((current) => {
        const demoted = data.price?.is_recommended
          ? current.map((price) => ({ ...price, is_recommended: false }))
          : current;
        return [...demoted, data.price];
      });
      setForm(EMPTY_FORM);
      setShowCreate(false);
      setNotice("Precio registrado correctamente.");
    } catch (err) {
      console.error("[ADMIN_PRICE_CREATE]", err);
      setError(err.message || "No fue posible crear el precio.");
    } finally {
      setSaving(false);
    }
  }

  async function patchPrice(priceId, patch, successMessage) {
    resetMessages();
    try {
      setBusyPriceId(priceId);
      const response = await fetch(`${API_URL}/api/admin/product-prices/${priceId}`, {
        method: "PATCH",
        headers: authHeaders(true),
        body: JSON.stringify(patch),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || "No fue posible actualizar el precio.");
      }

      setPrices((current) =>
        current.map((price) => {
          if (data.price?.is_recommended && price.id !== priceId) {
            return { ...price, is_recommended: false };
          }
          return price.id === priceId ? data.price : price;
        })
      );
      setNotice(successMessage);
    } catch (err) {
      console.error("[ADMIN_PRICE_PATCH]", err);
      setError(err.message || "No fue posible actualizar el precio.");
    } finally {
      setBusyPriceId(null);
    }
  }

  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Configuración comercial</div>
          <h3 style={titleStyle}>Precios y ofertas</h3>
          <p style={descriptionStyle}>
            Cada registro conserva sus términos comerciales. Para cambiar importe,
            moneda, modalidad o Stripe Price, crea un precio nuevo y desactiva el anterior.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            resetMessages();
            setShowCreate((current) => !current);
          }}
          style={primaryButtonStyle}
        >
          {showCreate ? "Cerrar" : "+ Añadir precio"}
        </button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}
      {notice && <div style={noticeStyle}>{notice}</div>}

      {showCreate && (
        <form onSubmit={createPrice} style={formStyle}>
          <div style={formGridStyle}>
            <Field label="Stripe Price ID" wide>
              <input
                value={form.stripe_price_id}
                onChange={(event) => updateForm("stripe_price_id", event.target.value)}
                placeholder="price_..."
                required
                style={inputStyle}
              />
            </Field>

            <Field label="Importe">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(event) => updateForm("amount", event.target.value)}
                placeholder="99.00"
                required
                style={inputStyle}
              />
            </Field>

            <Field label="Moneda">
              <input
                value={form.currency}
                onChange={(event) => updateForm("currency", event.target.value)}
                maxLength={3}
                required
                style={inputStyle}
              />
            </Field>

            <Field label="Modalidad">
              <select
                value={form.price_type}
                onChange={(event) => updateForm("price_type", event.target.value)}
                style={inputStyle}
              >
                <option value="one_time">Pago único</option>
                <option value="recurring">Recurrente</option>
              </select>
            </Field>

            {form.price_type === "recurring" && (
              <Field label="Intervalo">
                <select
                  value={form.interval}
                  onChange={(event) => updateForm("interval", event.target.value)}
                  style={inputStyle}
                >
                  <option value="month">Mensual</option>
                  <option value="year">Anual</option>
                </select>
              </Field>
            )}

            <Field label="Días de acceso">
              <input
                type="number"
                min="0"
                step="1"
                value={form.access_duration_days}
                onChange={(event) => updateForm("access_duration_days", event.target.value)}
                style={inputStyle}
              />
            </Field>

            <Field label="Orden">
              <input
                type="number"
                min="0"
                step="1"
                value={form.display_order}
                onChange={(event) => updateForm("display_order", event.target.value)}
                style={inputStyle}
              />
            </Field>

            <Field label="Etiqueta" wide>
              <input
                value={form.label}
                onChange={(event) => updateForm("label", event.target.value)}
                placeholder="Ej. Edición Fundadora"
                style={inputStyle}
              />
            </Field>

            <Field label="Nota comercial" wide>
              <textarea
                value={form.marketing_note}
                onChange={(event) => updateForm("marketing_note", event.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </Field>
          </div>

          <div style={checkRowStyle}>
            <label style={checkLabelStyle}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(event) => updateForm("is_active", event.target.checked)}
              />
              Activo
            </label>
            <label style={checkLabelStyle}>
              <input
                type="checkbox"
                checked={form.is_recommended}
                disabled={!form.is_active}
                onChange={(event) => updateForm("is_recommended", event.target.checked)}
              />
              Recomendado
            </label>
          </div>

          <div style={formFooterStyle}>
            <span style={helperStyle}>
              El primer precio activo se convierte automáticamente en recomendado.
            </span>
            <button type="submit" disabled={saving} style={primaryButtonStyle}>
              {saving ? "Guardando..." : "Guardar precio"}
            </button>
          </div>
        </form>
      )}

      {orderedPrices.length === 0 ? (
        <div style={emptyStyle}>
          Este producto todavía no tiene una oferta comercial registrada.
        </div>
      ) : (
        <div style={priceGridStyle}>
          {orderedPrices.map((price) => {
            const busy = busyPriceId === price.id;
            return (
              <article
                key={price.id}
                style={{
                  ...priceCardStyle,
                  ...(price.is_recommended ? recommendedCardStyle : {}),
                  ...(!price.is_active ? inactiveCardStyle : {}),
                }}
              >
                <div style={cardTopStyle}>
                  <div>
                    <div style={priceLabelStyle}>{price.label || "Precio"}</div>
                    <div style={moneyStyle}>{money(price.amount, price.currency)}</div>
                    <div style={modeStyle}>{paymentMode(price)}</div>
                  </div>

                  <div style={badgeStackStyle}>
                    {price.is_recommended && <span style={recommendedBadgeStyle}>Recomendado</span>}
                    <span style={price.is_active ? activeBadgeStyle : inactiveBadgeStyle}>
                      {price.is_active ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                </div>

                <dl style={detailsStyle}>
                  <Detail label="Stripe" value={price.stripe_price_id || "—"} mono />
                  <Detail
                    label="Acceso"
                    value={
                      Number(price.access_duration_days || 0) > 0
                        ? `${price.access_duration_days} días`
                        : "Sin duración definida"
                    }
                  />
                  <Detail label="Orden" value={String(price.display_order || 0)} />
                </dl>

                {price.marketing_note && (
                  <p style={marketingNoteStyle}>{price.marketing_note}</p>
                )}

                <div style={actionsStyle}>
                  {price.is_active && !price.is_recommended && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        patchPrice(
                          price.id,
                          { is_recommended: true },
                          "Precio recomendado actualizado."
                        )
                      }
                      style={secondaryButtonStyle}
                    >
                      Marcar recomendado
                    </button>
                  )}

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      patchPrice(
                        price.id,
                        { is_active: !price.is_active },
                        price.is_active ? "Precio desactivado." : "Precio reactivado."
                      )
                    }
                    style={price.is_active ? dangerButtonStyle : secondaryButtonStyle}
                  >
                    {busy ? "Guardando..." : price.is_active ? "Desactivar" : "Reactivar"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <label style={{ ...fieldStyle, ...(wide ? fieldWideStyle : {}) }}>
      <span style={fieldLabelStyle}>{label}</span>
      {children}
    </label>
  );
}

function Detail({ label, value, mono = false }) {
  return (
    <div>
      <dt style={detailLabelStyle}>{label}</dt>
      <dd style={{ ...detailValueStyle, ...(mono ? monoStyle : {}) }}>{value}</dd>
    </div>
  );
}

const sectionStyle = {
  border: "1px solid #e6ebf1",
  borderRadius: 16,
  padding: 18,
  background: "#fff",
  boxShadow: "0 10px 30px rgba(0,0,0,.05)",
  marginBottom: 20,
};
const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  alignItems: "flex-start",
  flexWrap: "wrap",
};
const eyebrowStyle = {
  fontSize: 11,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "#9a7634",
  fontWeight: 900,
  marginBottom: 5,
};
const titleStyle = { margin: 0, color: "#0a2230", fontSize: 20 };
const descriptionStyle = {
  margin: "6px 0 0",
  maxWidth: 660,
  color: "#6b7a88",
  lineHeight: 1.55,
  fontSize: 13,
};
const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 15px",
  border: 0,
  borderRadius: 10,
  background: "#264763",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};
const formStyle = {
  marginTop: 18,
  padding: 16,
  border: "1px solid #e6ebf1",
  borderRadius: 14,
  background: "#f8fafc",
};
const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};
const fieldStyle = { display: "grid", gap: 6 };
const fieldWideStyle = { gridColumn: "1 / -1" };
const fieldLabelStyle = { color: "#405064", fontSize: 12, fontWeight: 800 };
const inputStyle = {
  width: "100%",
  minHeight: 40,
  boxSizing: "border-box",
  border: "1px solid #d7dee8",
  borderRadius: 9,
  background: "#fff",
  padding: "9px 10px",
  color: "#213348",
  font: "inherit",
};
const checkRowStyle = { display: "flex", gap: 18, flexWrap: "wrap", marginTop: 14 };
const checkLabelStyle = {
  display: "inline-flex",
  gap: 7,
  alignItems: "center",
  color: "#405064",
  fontSize: 13,
  fontWeight: 700,
};
const formFooterStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginTop: 16,
};
const helperStyle = { color: "#7b8797", fontSize: 12, lineHeight: 1.45 };
const errorStyle = {
  marginTop: 14,
  padding: "10px 12px",
  borderRadius: 10,
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#b42318",
  fontSize: 13,
};
const noticeStyle = {
  marginTop: 14,
  padding: "10px 12px",
  borderRadius: 10,
  background: "#eefaf3",
  border: "1px solid #bbf7d0",
  color: "#166534",
  fontSize: 13,
};
const emptyStyle = {
  marginTop: 16,
  padding: 14,
  borderRadius: 12,
  background: "#f8fafc",
  color: "#64748b",
  fontSize: 13,
};
const priceGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))",
  gap: 12,
  marginTop: 16,
};
const priceCardStyle = {
  padding: 15,
  border: "1px solid #e6ebf1",
  borderRadius: 14,
  background: "#fafafa",
};
const recommendedCardStyle = { background: "#f5f9fc", border: "1px solid #bdd0df" };
const inactiveCardStyle = { opacity: 0.78, background: "#f8fafc" };
const cardTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
};
const priceLabelStyle = { color: "#6b7a88", fontSize: 12, fontWeight: 800 };
const moneyStyle = { marginTop: 4, color: "#0a2230", fontSize: 23, fontWeight: 900 };
const modeStyle = { marginTop: 3, color: "#64748b", fontSize: 12, fontWeight: 700 };
const badgeStackStyle = { display: "grid", justifyItems: "end", gap: 5 };
const badgeBaseStyle = {
  display: "inline-flex",
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 900,
  whiteSpace: "nowrap",
};
const recommendedBadgeStyle = {
  ...badgeBaseStyle,
  background: "#f7efd9",
  color: "#8a6424",
  border: "1px solid rgba(199,154,69,.35)",
};
const activeBadgeStyle = {
  ...badgeBaseStyle,
  background: "#eefaf3",
  color: "#166534",
  border: "1px solid #bbf7d0",
};
const inactiveBadgeStyle = {
  ...badgeBaseStyle,
  background: "#f1f5f9",
  color: "#64748b",
  border: "1px solid #dbe4ef",
};
const detailsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
  gap: 10,
  margin: "14px 0 0",
};
const detailLabelStyle = {
  color: "#8491a1",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: ".04em",
  fontWeight: 900,
};
const detailValueStyle = { margin: "4px 0 0", color: "#405064", fontSize: 12, fontWeight: 700 };
const monoStyle = { fontFamily: "monospace", wordBreak: "break-all", fontWeight: 600 };
const marketingNoteStyle = {
  margin: "12px 0 0",
  color: "#526274",
  fontSize: 12,
  lineHeight: 1.5,
};
const actionsStyle = { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 };
const secondaryButtonStyle = {
  minHeight: 36,
  padding: "0 11px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#264763",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 12,
};
const dangerButtonStyle = {
  ...secondaryButtonStyle,
  border: "1px solid #fecaca",
  color: "#b42318",
};
